import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE,
    OPEN, APPROVED, VERIFICATION,
    WORKFLOW_SEND_TO_LEVEL,
    WORKFLOW_RETURN_TO_LEVEL,
    WORKFLOW_APPROVED,
    REVIEW,
} from "const"
import {
    CcReviewReviewHistoryCreate,
    CcReviewReviewItemState,
    CcReviewReviewTempAnswer,
} from "server/models"
import {
    CcStateSettingsType,
    StateType,
    useInput,
} from "client/utils"
import {
    StoreType,
    ReviewReviewHistoryValidation,

    SaveCcReviewReviewTempAnswer,
    DeleteCcReviewReviewTempAnswers,
    CreateCcReviewReviewHistory,
    UpdateCcReviewReviewState,
    LoadCcReviewReviewItemState,
    LoadCcReviewReviewHistory,
    LoadCcReviewReviewTempAnswers,
    SetCcReviewHref,

    setCcReviewReviewItemHistory,
    setCcReviewReviewItemState,
    setCcReviewReviewReloadList,
    setCcReviewReviewTempAnswerList,
    setCcReviewReviewTempAnswers,
    closeModal,
} from "client/store"
import {
    Delimiter,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    UserIcon,

    SelectDropdown,
    SelectDropdownItem,
    ButtonGroup,
    ButtonGroupItem,

    Button,
    SpinnerIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
} from "client/components"


const RETURN = "RETURN"
const SUBMIT = "SUBMIT"

interface EditReviewReviewHistoryProps {
    history: CcReviewReviewHistoryCreate
}

let isMounted: boolean

const Form = ({
    history,
}: EditReviewReviewHistoryProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccReviewDocument.baseRoute)
    const activeFile = useSelector((state: StoreType) => state.ccReviewDocument.activeFile)
    const activeVersion = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)

    const revisionActiveList = useSelector((state: StoreType) => state.ccReviewReview.revisionActiveList)
    const itemDetails = useSelector((state: StoreType) => state.ccReviewReview.itemDetails)
    const itemState = useSelector((state: StoreType) => state.ccReviewReview.itemState)

    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[2] || [] // review level ids

    const groupUserList = useSelector((state: StoreType) => state.projectUserGroup.groupUserList)
    const userList = useSelector((state: StoreType) => state.user.userList)

    const responseList = useSelector((state: StoreType) => state.ccResponse.responseList)
    const moduleResponseList = useSelector((state: StoreType) => state.ccResponse.moduleResponseList)
    const responseIds = moduleResponseList[3]
    const responsibleResponseIds = moduleResponseList[0]

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [remark, setRemark] = useInput(history.remark)
    const [nextRevision, setNextRevision] = useInput(history.nextRevision)

    const [responseId, setResponseId] = useState(history.responseId)
    const [nextUserId, setNextUserId] = useState(history.nextUserId)

    const [confirmType, setConfirmType] = useState<typeof RETURN | typeof SUBMIT>(null)
    const [showConfirm, setShowConfirm] = useState(false)

    const [validation, setValidation] = useState<ReviewReviewHistoryValidation>(null)
    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [confirmState, setConfirmState] = useState<StateType>(IDLE)

    const revision = revisionActiveList[itemDetails.id]

    const isFirstLevel = history.prevLevelId === levelIds[0]
    const firstLevel = levelIds[0] && levelList[levelIds[0]]

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onSave = () => {
        setSaveState(PENDING)
        const tempAnswer: CcReviewReviewTempAnswer = {
            itemId: history.itemId,
            reviewReviewRevisionId: history.reviewReviewRevisionId,
            editorId: userData.userId,
            responseId,
            remark,
            nextRevision,
            nextUserId,
        }
        SaveCcReviewReviewTempAnswer(tempAnswer, lang).then((res) => {
            if (res) { // validation not empty
                if (isMounted) {
                    setValidation(res)
                    setSaveState(IDLE)
                }
            } else {
                dispatch(setCcReviewReviewTempAnswerList(history.itemId, { [userData.userId]: tempAnswer }))
                if (isMounted) setSaveState(IDLE)
            }
        })
    }

    const onConfirm = () => {
        setConfirmState(PENDING)
        const settings: CcStateSettingsType = itemState && itemState.settings ? JSON.parse(itemState.settings) : {}

        let newHistory: CcReviewReviewHistoryCreate = {
            ...history,
            nextUserId,
            responseId,
            remark,
            nextRevision,
        }

        if (isFirstLevel) {
            newHistory = {
                ...newHistory,
                workflow: WORKFLOW_SEND_TO_LEVEL,
            }
        }

        if (!isFirstLevel) {
            // return to first level
            if (confirmType === RETURN) {
                newHistory = {
                    ...newHistory,
                    nextLevelId: levelIds[0] || null,
                    nextUserId: (levelIds[0] && settings[levelIds[0]]) || null,
                    nextStatus: OPEN,
                    workflow: WORKFLOW_RETURN_TO_LEVEL,
                }
            }

            if (confirmType === SUBMIT) {
                if (newHistory.nextLevelId === null) { // the last approval level
                    newHistory = {
                        ...newHistory,
                        nextStatus: APPROVED,
                        workflow: WORKFLOW_APPROVED,
                    }
                } else { // send to next level
                    newHistory = {
                        ...newHistory,
                        nextStatus: VERIFICATION,
                        workflow: WORKFLOW_SEND_TO_LEVEL,
                    }
                }
            }
        }

        CreateCcReviewReviewHistory(newHistory, lang)
        .then((historyRes) => {
            setValidation(historyRes.validation)

            if (historyRes.newHistoryId) {
                settings[newHistory.nextLevelId] = newHistory.nextUserId

                const newState: CcReviewReviewItemState = {
                    itemId: newHistory.itemId,
                    reviewReviewRevisionId: newHistory.reviewReviewRevisionId,
                    levelId: newHistory.nextLevelId,
                    verifierId: newHistory.nextUserId,
                    settings: JSON.stringify(settings),
                }

                Promise.all([
                    UpdateCcReviewReviewState(newState),
                    DeleteCcReviewReviewTempAnswers(itemDetails.id, revision.id),
                ]).then(() => {
                    // form will be rerendered automatically
                    LoadCcReviewReviewItemState(itemDetails.id, revision.id).then((res) => {
                        dispatch(setCcReviewReviewItemState(res))
                    })
                    LoadCcReviewReviewHistory(itemDetails.id, revision.id).then((res) => {
                        dispatch(setCcReviewReviewItemHistory(res))
                    })
                    LoadCcReviewReviewTempAnswers(itemDetails.id, revision.id).then((res) => {
                        dispatch(setCcReviewReviewTempAnswers(res))
                    })

                    // reload review list
                    dispatch(setCcReviewReviewReloadList(false))
                    setTimeout(() => {
                        dispatch(setCcReviewReviewReloadList(true))
                    }, 0)
                })
            } else if (isMounted) {
                setConfirmState(IDLE)
                setShowConfirm(false)
            }
        })
    }

    const closeConfirm = () => {
        setShowConfirm(false)
    }

    const onCancel = () => {
        SetCcReviewHref(baseRoute, REVIEW, activeFile.id, activeVersion.id)
        dispatch(closeModal())
    }

    const nextLevel = levelList[history.nextLevelId]
    const nextUser = userList[nextUserId]

    const groupId = nextLevel ? nextLevel.groupId : null
    const userIds = (groupUserList && groupUserList[groupId]) || null

    let disableSave = false
    let disableReturn = false

    // first level user didn't provide answer/response or remark
    if (isFirstLevel) {
        if (
            !responseId
            || remark.trim().length === 0
            || nextRevision.trim().length === 0
        ) {
            disableSave = true
        }
    }

    // other level user didn't provide response or remark
    if (!isFirstLevel) {
        if (
            !responseId
            || remark.trim().length === 0
        ) {
            disableSave = true
            disableReturn = true
        }
    }

    return (
        <Row>
            <Col xs={12}>
                <Delimiter />

                <FormGroup>
                    <Label>{lang.response}</Label>
                    <ButtonGroup>
                        {
                            isFirstLevel
                                ? ( // responses for responsible
                                    responsibleResponseIds && responsibleResponseIds.map((id) => (
                                        <ButtonGroupItem
                                            key={id}
                                            active={responseId === id}
                                            onClick={() => setResponseId(id)}
                                        >
                                            {responseList[id] ? responseList[id].response : ""}
                                        </ButtonGroupItem>
                                    ))
                                )
                                : ( // responses for verifier
                                    responseIds && responseIds.map((id) => (
                                        <ButtonGroupItem
                                            key={id}
                                            active={responseId === id}
                                            onClick={() => setResponseId(id)}
                                        >
                                            {responseList[id] ? responseList[id].response : ""}
                                        </ButtonGroupItem>
                                    ))
                                )
                        }
                    </ButtonGroup>
                    <Feedback>{validation && validation.responseId}</Feedback>
                </FormGroup>

                <FormGroup>
                    <Label>{lang.remark}</Label>
                    <Textarea value={remark} onChange={setRemark} />
                    <Feedback>{validation && validation.remark}</Feedback>
                </FormGroup>

                {
                    isFirstLevel && ( // only for responsible
                        <FormGroup>
                            <Label>{lang.addressedInRevision}</Label>
                            <Input type="text" value={nextRevision} onChange={setNextRevision} />
                        </FormGroup>
                    )
                }
                {
                    // nextLevel is null on the last approval level
                    nextLevel && (
                        <FormGroup>
                            <Label>{lang.approvalLevel}</Label>
                            <Input type="text" value={nextLevel.name} readOnly />
                        </FormGroup>
                    )
                }
                {
                    // users are empty on the last approval level
                    userIds && (
                        <FormGroup>
                            <Label>{lang.assignTo}</Label>
                            <SelectDropdown title={nextUser ? <UserIcon user={nextUser} size="sm" /> : lang.selectUser}>
                                {
                                    userIds.map((userId) => (
                                        <SelectDropdownItem
                                            key={userId}
                                            size="sm"
                                            onClick={() => setNextUserId(userId)}
                                        >
                                            <UserIcon user={userList[userId]} size="sm" />
                                        </SelectDropdownItem>
                                    ))
                                }
                            </SelectDropdown>
                        </FormGroup>
                    )
                }
            </Col>
            <Col xs={12}>
                <Button row variant="success" onClick={onSave}>
                    {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                </Button>
                <Button
                    row
                    variant="success"
                    disabled={disableSave}
                    onClick={() => {
                        setConfirmType(SUBMIT)
                        setShowConfirm(true)
                    }}
                >
                    {lang.submit}
                </Button>
                {
                    !isFirstLevel && (
                        <Button
                            row
                            variant="danger"
                            disabled={disableReturn}
                            onClick={() => {
                                setConfirmType(RETURN)
                                setShowConfirm(true)
                            }}
                        >
                            {lang.returnToLevel.replace("{level}", firstLevel ? firstLevel.name : "")}
                        </Button>
                    )
                }
                <Button row onClick={onCancel}>{lang.cancel}</Button>
            </Col>

            <ConfirmWrapper
                show={showConfirm}
                onClose={closeConfirm}
            >
                <Confirm>
                    <ConfirmTitle>
                        {confirmType === RETURN && lang.returnToLevel.replace("{level}", firstLevel ? firstLevel.name : "")}
                        {confirmType === SUBMIT && lang.submit}
                    </ConfirmTitle>
                    <ConfirmDescription>{lang.confirmCompliance}</ConfirmDescription>

                    <ConfirmBottom>
                        <Button row onClick={closeConfirm}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirm}>
                            {confirmState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </Row>
    )
}
export default Form
